/* eslint-disable no-param-reassign */
import { each } from 'underscore';
import React, { useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import classnames from 'classnames';
import AdScheduleView from '@bingads-webui-campaign-legacy/ad-schedule/view/index';
import PropTypes from 'prop-types';
import { advancedSettingsSectionStyle, toggleButtonStyle, datePickerStyle } from '@bingads-webui-campaign-react/common-styles';
import {
  creationCommonStyle
} from '@bingads-webui-campaign-react/campaign-adgroup-settings-shared-styles';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

const getStyles = (__, core) =>
  ({
    '@global': {
      ...datePickerStyle(core),
    },
    adScheduleContainer: {
      '& .advanced-scheduling-grid': {
        width: '918px',
      },
      ...creationCommonStyle(core),
      ...advancedSettingsSectionStyle(core),
      '& .radio-inline': {
        display: 'block',
      },
      '& .adschedule-targets-container': {
        borderTop: 'unset',
        paddingTop: '0px',
      },
      '& .toggle-campaign-settings-switch.form-control-static[data-bind]': {
        display: 'inline-block',
        textDecoration: 'none',
        paddingRight: '8px',
        float: 'left',
      },
      ...toggleButtonStyle(core),
      '& .date-settings-intro': {
        paddingLeft: 0,
      },
      '& .date-settings': {
        '& .col-sm-12': {
          paddingLeft: 0,
        },
        '& table': {
          width: '500px',
          '& td': {
            width: '100%',
            display: 'block',
          },
        },
      },
      '& .alert-danger': {
        backgroundColor: 'unset',
        color: '#A80000',
      },
      '& table tbody .col-sm-9': {
        width: 'unset',
        paddingLeft: 'unset',
      },
      '& table tbody .item-label.schedule-item-label.form-control-static': {
        fontWeight: '600',
        color: '#323130',
        paddingTop: '10px',
        display: 'block',
      },
      '& table tbody  .form-control:focus': {
        outline: '1px solid #0078d4',
      },
      '& .adschedule-targets-container .advanced-scheduling-grid .ad-scheduling-panel table tr:nth-child(3) td .radio-inline:nth-child(2)': {
        top: 'unset',
      },
      '& .alert-info': {
        '& .alert-info-inline': {
          '& a:focus': {
            outline: '1px solid #605e5c',
          },
        },
      },
    },
  });

export const LegacyAdSchedule = React.memo((props) => {
  const {
    entity,
    data,
    campaignDayTimeRanges,
    onChange,
    setAdScheduleView,
    level,
    hideUseCampaignSetting,
    hideMinuteOptions,
    showDateSettings,
    showBidAdjustmentColumn,
    i18nKeys,
    campaignAdScheduleUseSearcherTimeZone,
  } = props;
  const view = useRef();

  const activity = useContext(ActivityContext);

  let container;
  const classes = useDefaultStyles(getStyles);
  const deletedDayTimeCb = useCallback((deletedDayTimeCriterions) => {
    each(deletedDayTimeCriterions, (criterion) => {
      criterion.Criterion.BidAdjustment = 0;
      criterion.Status = 'Deleted';
      criterion.Campaign = {
        Id: entity.data.Id,
      };
    });
  }, [entity.data.Id]);

  const adScheduleOption = useMemo(
    () => {
      const model = {
        dayTimeRanges: data.dayTimeRanges,
        timeZone: entity.data.TimeZone,
        adScheduleUseSearcherTimeZone: entity.data.AdScheduleUseSearcherTimeZone,
      };
      return {
        model,
        i18nKeys,
        level,
        hideMinuteOptions,
        showDateSettings,
        hideAdScheduleBidAdjustments: false,
        showBidAdjustmentColumn,
        hideUseCampaignSetting,
        campaignDayTimeRanges,
        campaignAdScheduleUseSearcherTimeZone,
        activityLogger: activity,
        onChange: (newData, isValid) => {
          const updatedData = {
            adSchedulesBulkUpsert: newData,
            AdScheduleUseSearcherTimeZone: view.current.getAdScheduleUseSearcherTimeZone(),
          };

          if (showDateSettings) {
            updatedData.adScheduleDateSettings = view.current.getDateSettings();
          }

          onChange(updatedData, isValid);
        },
        deleteCb: deletedDayTimeCb,
      };
    },
    [data.dayTimeRanges, entity.data.TimeZone, entity.data.AdScheduleUseSearcherTimeZone, i18nKeys, level, hideMinuteOptions, showDateSettings, showBidAdjustmentColumn, hideUseCampaignSetting, campaignDayTimeRanges, activity, deletedDayTimeCb, onChange]
  );

  useEffect(() => {
    view.current = new AdScheduleView({
      ...adScheduleOption,
    });

    view.current.render();
    view.current.$el.appendTo(container);
    setAdScheduleView(view);
    onChange(view.current.getValues(deletedDayTimeCb), true, false);

    return () => {
      view.current.remove();
    };
    // We will refresh the view if data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.dayTimeRanges]);

  return (
    <div
      className={classnames(classes.adScheduleContainer)}
    >
      <div className="adschedule-targets-container">
        <div ref={(ref) => { container = ref; }} />
      </div>
    </div>
  );
});

LegacyAdSchedule.propTypes = {
  entity: PropTypes.shape({
    data: PropTypes.shape({
      Id: PropTypes.number,
      TimeZone: PropTypes.shape({}),
      AdScheduleUseSearcherTimeZone: PropTypes.shape({}),
    }),
  }),
  data: PropTypes.shape({
    dayTimeRanges: PropTypes.shape({}),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  setAdScheduleView: PropTypes.func,
  level: PropTypes.string,
  hideUseCampaignSetting: PropTypes.bool,
  hideMinuteOptions: PropTypes.bool,
  showDateSettings: PropTypes.bool,
  showBidAdjustmentColumn: PropTypes.bool,
  campaignDayTimeRanges: PropTypes.shape({}),
  i18nKeys: PropTypes.shape({}),
  campaignAdScheduleUseSearcherTimeZone: PropTypes.bool,
};

LegacyAdSchedule.defaultProps = {
  entity: {},
  level: 'campaign',
  setAdScheduleView: () => {},
  hideUseCampaignSetting: false,
  hideMinuteOptions: false,
  showDateSettings: false,
  showBidAdjustmentColumn: false,
  campaignDayTimeRanges: {},
  i18nKeys: {
    Warning: 'TargetingSettings_AdSchedule_Warning_PMax',
  },
  campaignAdScheduleUseSearcherTimeZone: false,
};
